/**
 * Cuánto aguanta una contraseña, y una nueva cuando hace falta.
 *
 * ⚠ **`fuerzaDe` es la MISMA regla que `fuerza_de()` en `services/credenciales.py`.**
 * El servidor la calcula al guardar —para el listado, que no descifra nada— y la
 * pantalla mientras se escribe. Si una cambia y la otra no, la barra del formulario y
 * la de la ficha guardada dirán cosas distintas de la misma clave.
 *
 * ⚠ **Es una orientación, no una política.** No se rechaza ninguna: la contraseña de un
 * portal la impone el banco o el SENIAT, no nosotros.
 */

export const NOMBRE_FUERZA = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte"] as const

export function fuerzaDe(clave: string): number {
  // Por puntos de código y no por `length`: igual que `len()` en el servidor.
  const largo = [...clave].length
  let puntos = 0
  if (largo >= 8) puntos += 1
  if (largo >= 12) puntos += 1
  if (/\p{Ll}/u.test(clave) && /\p{Lu}/u.test(clave)) puntos += 1
  if (/\p{Nd}/u.test(clave) && /[^\p{L}\p{N}]/u.test(clave)) puntos += 1
  return puntos
}

/**
 * Sin los caracteres que se confunden —l, I, 1, O, 0—: una clave que se dicta por
 * teléfono o se copia de un papel no puede depender de distinguirlos.
 */
export const ALFABETO = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!#$%&*+-=?@_"

/**
 * Una contraseña nueva, de `crypto.getRandomValues` y nunca de `Math.random`.
 *
 * Se vuelve a sortear hasta que puntúa 4: dieciséis caracteres al azar casi siempre lo
 * hacen, y una clave generada que la barra llame «Buena» haría dudar del generador.
 */
export function generarClave(largo = 16): string {
  let salida = sortear(largo)
  for (let intento = 0; intento < 20 && largo >= 12 && fuerzaDe(salida) < 4; intento += 1) {
    salida = sortear(largo)
  }
  return salida
}

/**
 * ⚠ **Por rechazo, para que no haya sesgo.** `valor % 69` sobre un entero de 32 bits
 * favorece a los primeros caracteres del alfabeto; descartando el último tramo
 * incompleto, todos salen con la misma probabilidad.
 */
function sortear(largo: number): string {
  const n = ALFABETO.length
  const tope = Math.floor(0x1_0000_0000 / n) * n
  const muestra = new Uint32Array(1)
  let salida = ""
  while (salida.length < largo) {
    crypto.getRandomValues(muestra)
    const valor = muestra[0] ?? tope
    if (valor < tope) salida += ALFABETO.charAt(valor % n)
  }
  return salida
}
